import AxiosInstance from '../axios/AxiosInstance';
import { API_ENDPOINTS, RELEVE_HEURES_STATUS } from '../config/apiEndpoints';
import { primeRate } from '../utils/primeCatalog';

class ReleveHeuresService {
  clearMyReleveHeuresCache() {
    this.myRelevesCache = null;
  }

  getMyReleveHeures() {
    const scope = JSON.stringify([localStorage.getItem('accessToken'), localStorage.getItem('agencyId'), localStorage.getItem('agencyName')]);
    const cached = this.myRelevesCache;
    if (cached?.scope === scope) {
      if (cached.pending) return cached.pending;
      if (Date.now() - cached.savedAt < 30000) return Promise.resolve(cached.rows);
    }
    const entry = { scope };
    this.myRelevesCache = entry;
    entry.pending = this.loadMyReleveHeures().then(rows => {
      entry.rows = rows;
      entry.savedAt = Date.now();
      entry.pending = null;
      return rows;
    }, error => {
      if (this.myRelevesCache === entry) this.clearMyReleveHeuresCache();
      throw error;
    });
    return entry.pending;
  }

  // Récupérer tous les relevés d'heures (pour admin/agence)
  async getAllReleveHeures() {
    try {
      const response = await AxiosInstance.get(API_ENDPOINTS.RELEVE_HEURES.GET_ALL);
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des relevés d\'heures:', error);

      // Si c'est une erreur 404, retourner un tableau vide
      if (error.response?.status === 404) {
        console.warn('Aucun relevé d\'heures trouvé (404)');
        return [];
      }

      // Pour les autres erreurs, les propager
      throw error;
    }
  }

  // Récupérer les relevés d'heures pour le client connecté (freelance)
  async getReleveHeuresByClient() {
    try {
      const response = await AxiosInstance.get(API_ENDPOINTS.RELEVE_HEURES.GET_BY_CLIENT);
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des relevés d\'heures par client:', error);

      // Si c'est une erreur 404, retourner un tableau vide
      if (error.response?.status === 404) {
        console.warn('Aucun relevé d\'heures trouvé pour ce client (404)');
        return [];
      }

      throw error;
    }
  }

  // Récupérer les relevés d'heures pour le freelance connecté (méthode principale)
  async loadMyReleveHeures() {
    try {
      const clientsResponse = await AxiosInstance.get('client/freelance');
      if (!Array.isArray(clientsResponse.data)) throw new Error('Liste des clients invalide');
      const clientIds = new Set(clientsResponse.data
        .map(client => client?.pld == null ? '' : String(client.pld).trim())
        .filter(Boolean));
      if (!clientIds.size) return [];

      const response = await AxiosInstance.get(API_ENDPOINTS.RELEVE_HEURES.GET_BY_FREELANCE);
      if (!Array.isArray(response.data)) throw new Error('Liste des relevés invalide');
      // IdClient est l’identifiant PLD, pas l’identifiant MongoDB du client.
      return response.data.filter(releve => releve?.IdClient != null && clientIds.has(String(releve.IdClient).trim()));
    } catch (error) {
      console.error('Erreur lors de la récupération de mes relevés d\'heures:', error);

      // Si c'est une erreur 404, retourner un tableau vide
      if (error.response?.status === 404) {
        console.warn('Aucun relevé d\'heures trouvé pour ce freelance (404)');
        return [];
      }

      throw error;
    }
  }

  // Récupérer les motifs d'absence
  async getMotifAbsence() {
    try {
      const response = await AxiosInstance.get(API_ENDPOINTS.RELEVE_HEURES.GET_MOTIF_ABSENCE);
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des motifs d\'absence:', error);

      // Si c'est une erreur 404, retourner un tableau vide
      if (error.response?.status === 404) {
        console.warn('Aucun motif d\'absence trouvé (404)');
        return [];
      }

      throw error;
    }
  }

  // Créer un nouveau relevé d'heures (envoi vers Tempo)
  async createReleveHeure(releveData) {
    try {
      const response = await AxiosInstance.post(API_ENDPOINTS.RELEVE_HEURES.CREATE, releveData);
      this.clearMyReleveHeuresCache();
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du relevé d\'heures:', error);
      throw error;
    }
  }

  async getAttachment(id) {
    const { data } = await AxiosInstance.get(`${API_ENDPOINTS.RELEVE_HEURES.BASE}/${id}/attachment`);
    const bytes = Uint8Array.from(atob(data.data), char => char.charCodeAt(0));
    return new File([bytes], data.name, { type: data.type });
  }

  async setAttachment(id, file) {
    const body = new FormData();
    body.append('file', file);
    await AxiosInstance.put(`${API_ENDPOINTS.RELEVE_HEURES.BASE}/${id}/attachment`, body);
    this.clearMyReleveHeuresCache();
  }

  async removeAttachment(id) {
    await AxiosInstance.delete(`${API_ENDPOINTS.RELEVE_HEURES.BASE}/${id}/attachment`);
    this.clearMyReleveHeuresCache();
  }

  async markTransmitted(id) {
    const { data } = await AxiosInstance.post(`${API_ENDPOINTS.RELEVE_HEURES.BASE}/${id}/transmit`);
    this.clearMyReleveHeuresCache();
    return data;
  }

  async addHistory(ids, action, week, year) {
    const { data } = await AxiosInstance.post(`${API_ENDPOINTS.RELEVE_HEURES.BASE}/history`, { ids, action, week, year });
    this.clearMyReleveHeuresCache();
    return data;
  }

  // Mettre à jour un relevé d'heures
  async updateReleveHeure(id, releveData) {
    try {
      const formatted = this.transformToBackendFormat(releveData);
      const response = await AxiosInstance.patch(API_ENDPOINTS.RELEVE_HEURES.UPDATE(id), {
        TabPrimes: formatted.TabPrimes,
        TabHeures: formatted.TabHeures,
        CommentaireClient: releveData.clientComment || '',
        CommentaireInterimaire: releveData.employeeComment || '',
      });
      this.clearMyReleveHeuresCache();
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du relevé d\'heures:', error);
      throw error;
    }
  }

  // Transformer les données du frontend vers le format backend
  transformToBackendFormat(timeSheetData) {
    // Transformer les jours
    const TabJours = Object.entries(timeSheetData.hours || {}).map((entry, index) => {
      const [dayKey, hours] = entry;
      const dayNames = {
        'lundi': 'Lundi',
        'mardi': 'Mardi', 
        'mercredi': 'Mercredi',
        'jeudi': 'Jeudi',
        'vendredi': 'Vendredi',
        'samedi': 'Samedi',
        'dimanche': 'Dimanche'
      };

      return {
        NumeroJour: (index + 1).toString(),
        NomJour: dayNames[dayKey] || dayKey,
        DateJour: this.getDateForDay(dayKey, timeSheetData.startDate),
        NbHeureJour: hours || "0",
        NbHeureNuit: timeSheetData.nightHours?.[dayKey] || "0",
        Chantier: timeSheetData.projectNumber || "",
        IdMotifAbsence: timeSheetData.absences?.[dayKey] ? "1" : "",
        TxtMotifAbsence: timeSheetData.absences?.[dayKey] || ""
      };
    });

    // Transformer les heures (types d'heures)
    const rateText = value => {
      const rate = primeRate(value);
      return rate == null ? '0' : String(rate);
    };
    const TabHeures = (timeSheetData.heures || []).map((heure, index) => ({
      NumeroHeure: (index + 1).toString(),
      TxtHeure: heure.type || "",
      NbHeurePaye: heure.quantite?.toString() || "0",
      TauxPaye: rateText(heure.taux),
      NbHeureFacture: heure.quantite?.toString() || "0",
      TauxFacture: rateText(heure.tauxFacture ?? heure.taux)
    }));

    const TabPrimes = (timeSheetData.primes || []).map((prime, index) => ({
      IdPrime: String(prime.idPrime || prime.id || index + 1),
      TxtPrime: prime.type || "",
      LibPrimeComplement: "",
      MethodeCalcul: "1",
      QuantiteImposable: prime.nonAssujettie ? "0" : (prime.quantite?.toString() || "0"),
      TauxPayeImposable: prime.nonAssujettie ? "0" : rateText(prime.montant),
      QuantiteNonImposable: prime.nonAssujettie ? (prime.quantite?.toString() || "0") : "0",
      TauxPayeNonImposable: prime.nonAssujettie ? rateText(prime.montant) : "0",
      QuantiteFacture: prime.quantite?.toString() || "0",
      TauxFacture: rateText(prime.tauxFacture ?? prime.montant)
    }));

    return {
      NumeroContrat: timeSheetData.contractNumber || "",
      IdClient: timeSheetData.clientId || "",
      IdInterimaire: timeSheetData.employeeId || "",
      DateDebut: timeSheetData.startDate || "",
      DateFin: timeSheetData.endDate || "",
      TabJours,
      TabHeures,
      TabPrimes
    };
  }

  // Transformer les données du backend vers le format frontend
  transformToFrontendFormat(backendData) {
    const hours = {};
    const nightHours = {};
    const absences = {};

    // Transformer les jours
    if (backendData.TabJours && Array.isArray(backendData.TabJours)) {
      backendData.TabJours.forEach(jour => {
        if (jour && jour.NomJour) {
          const dayKey = jour.NomJour.toLowerCase();
          hours[dayKey] = jour.NbHeureJour || "";
          nightHours[dayKey] = jour.NbHeureNuit || "";
          absences[dayKey] = jour.TxtMotifAbsence || "";
        }
      });
    }

    // Transformer les heures
    const heures = (backendData.TabHeures || []).map(heure => ({
      id: heure?.NumeroHeure || "",
      type: heure?.TxtHeure || "",
      quantite: parseFloat(heure?.NbHeurePaye || 0) || 0,
      taux: primeRate(heure?.TauxPaye),
      tauxFacture: primeRate(heure?.TauxFacture)
    }));

    const primes = (backendData.TabPrimes || []).flatMap((prime, index) => {
      const idPrime = prime?.IdPrime != null && String(prime.IdPrime) !== '' ? String(prime.IdPrime) : '';
      const id = idPrime || `prime-${index}`;
      const base = { type: prime?.TxtPrime || "", idPrime, tauxFacture: primeRate(prime?.TauxFacture) };
      const taxable = parseFloat(prime?.QuantiteImposable || 0) || 0;
      const nonTaxable = parseFloat(prime?.QuantiteNonImposable || 0) || 0;
      const taxableRate = primeRate(prime?.TauxPayeImposable);
      const untaxedRate = primeRate(prime?.TauxPayeNonImposable);
      const rows = [];
      if (taxable || taxableRate != null || (!nonTaxable && untaxedRate == null)) {
        rows.push({ ...base, id, quantite: taxable, montant: taxableRate });
      }
      if (nonTaxable || untaxedRate != null) {
        rows.push({ ...base, id: `${id}-non-assujettie`, quantite: nonTaxable, montant: untaxedRate, nonAssujettie: true });
      }
      return rows;
    });

    return {
      id: backendData?._id || "",
      contractNumber: backendData?.NumeroContrat ?? "",
      amendmentNumber: backendData?.NumeroAvenant ?? "",
      clientId: backendData?.IdClient || "",
      clientName: backendData?.RaisonSociale || "",
      employeeId: backendData?.IdInterimaire || "",
      employeeName: backendData?.NomPrenom || "",
      employeePosition: backendData?.Profession || "",
      startDate: backendData?.DateDebut || "",
      endDate: backendData?.DateFin || "",
      hours,
      nightHours,
      absences,
      heures,
      primes,
      projectNumber: backendData?.TabJours?.[0]?.Chantier || "",
      status: this.determineStatus(backendData || {}),
      endOfMission: backendData?.FinDeMission === "OUI",
      attachmentName: backendData?.attachment?.name || "",
      transmittedAt: backendData?.transmittedAt || null,
      history: Array.isArray(backendData?.history) ? backendData.history : []
    };
  }

  // Déterminer le statut basé sur les données backend
  determineStatus(backendData) {
    if (backendData.DateFacturation) return RELEVE_HEURES_STATUS.INTEGRATED;
    if (backendData.RHBloque === "OUI") return RELEVE_HEURES_STATUS.BLOCKED;
    if (backendData.transmittedAt) return RELEVE_HEURES_STATUS.TO_INTEGRATE;
    if (backendData.BlocagePaie === "OUI") return RELEVE_HEURES_STATUS.PENDING;
    return RELEVE_HEURES_STATUS.A_SAISIR;
  }

  // Calculer la date pour un jour donné
  getDateForDay(dayKey, startDateStr) {
    const dayIndex = {
      'lundi': 0,
      'mardi': 1,
      'mercredi': 2,
      'jeudi': 3,
      'vendredi': 4,
      'samedi': 5,
      'dimanche': 6
    };

    if (!startDateStr) return "";

    try {
      const [day, month, year] = startDateStr.split('/');
      const startDate = new Date(year, month - 1, day);
      const targetDate = new Date(startDate);
      targetDate.setDate(startDate.getDate() + dayIndex[dayKey]);
      
      return targetDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch (error) {
      console.error('Erreur lors du calcul de la date:', error);
      return "";
    }
  }

  // Calculer le total des heures
  calculateTotalHours(hours) {
    return Object.values(hours || {}).reduce((total, hour) => {
      return total + (parseFloat(hour) || 0);
    }, 0);
  }

  // Valider les données avant envoi
  validateTimeSheetData(timeSheetData) {
    const errors = [];

    if (!timeSheetData.contractNumber) {
      errors.push("Le numéro de contrat est requis");
    }

    if (!timeSheetData.clientId) {
      errors.push("L'ID client est requis");
    }

    if (!timeSheetData.employeeId) {
      errors.push("L'ID intérimaire est requis");
    }

    if (!timeSheetData.startDate || !timeSheetData.endDate) {
      errors.push("Les dates de début et fin sont requises");
    }

    const totalHours = this.calculateTotalHours(timeSheetData.hours);
    if (totalHours === 0) {
      errors.push("Au moins une heure doit être saisie");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

const releveHeuresService = new ReleveHeuresService();

export default releveHeuresService;
